/*
 * Copyright 2025 iodyne.  All rights reserved.
 * Use is subject to license terms.
 */

import { createEntityAdapter, type EntityState } from "@reduxjs/toolkit"
import { createApi, fetchBaseQuery, type FetchBaseQueryMeta} from "@reduxjs/toolkit/query/react";
import { AuthMode, DeviceCategory, type ContainerInfo, type DeviceCapacity, type DeviceInfo, type IodyneDeviceDetail, type IodyneDeviceSecret} from "@/lib/models/devices";
import { decodeAPIError, type ModelError } from "@/lib/models/errors"
import { translate as t} from "@/i18n/client_nohook";
import { AuthStates, getAuthState } from "@/lib/auth/auth_state";
import { responseForAPIError } from "@/lib/datastore/slices/helpers";

// Endpoint Response Types
export interface DeviceType {
  enabled: boolean,
  fguid: string,
  target_fguid?: string,
  aliased_fguids: string[],
  device_auth_pub_key?: string,
  addl_product_skus: Array<String>,
  customer_registration_date: number,
  first_poweron_date?: number,
  auth_mode?:AuthMode,
  primary_contact: {
    email?: string,
    display_note?: string
  },
  device_info: DeviceInfo,
  telemetry_enabled?: boolean,
  provisioned_acct_kguid?: string,
  provisioned_acct_pub_key?:{
    kid: string,
    kty: string
    use: string,
    crv: string,
    x: string,
    y: string,
    iss?: string,
    exp?: string,
    kguid?: string
  },
  projects: Array<[string, string]>,
  retained_until_ts?: string,
  tags: Array<string>,
  location? : {
    lat: string,
    long: string,
    last_updated_ts: number
  }
};

// Constants
enum DeviceAPIErrors {
  deviceDNEError = "DeviceDNEError",
  accountDNEError = "AccountDNEError",
  customerDeviceDNEError = "CustomerDeviceDNEError",
  deviceSecretDNEError = "DeviceSecretDNEError"
}

// Slices
const devicesAdapter = createEntityAdapter({
  selectId: (e:IodyneDeviceDetail) => e.fguid
});
const devicesInitialState = devicesAdapter.getInitialState();

// Helpers
const deviceResponseTransform = (resp: DeviceType) => {
  return {
    fguid: resp.fguid,
    target_fguid: resp.target_fguid,
    aliased_fguids: resp.aliased_fguids,
    enabled: resp.enabled,
    customer_registration_date: resp.customer_registration_date,
    dev_auth_pub_key: resp.device_auth_pub_key,
    projects: resp.projects,
    tags: resp.tags,
    addl_product_skus: [], // TODO: Update devices API to return this.
    device_info: resp.device_info,
    location: resp.location,
    auth_mode: resp.auth_mode
  } as IodyneDeviceDetail;
}

export const devicesSlice = createApi({
  reducerPath: "devices_api",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.IOD_CLOUD_PORTAL_DEVICES_API_URI}/v2/`,
    prepareHeaders: (headers, { getState }) => {
      headers.set("Authorization", `Bearer ${getAuthState(AuthStates.userAccessToken)}`)
      return headers;
    }
  }),
  tagTypes: ["Device", "DeviceTags", "DeviceContainerList", "DeviceSecret"],
  endpoints: build => ({
    // Retrieve Device Detail
    fetchDeviceDetail: build.query<EntityState<IodyneDeviceDetail, string>, { fguid: string }>({
      providesTags: (result, _, arg) => [
        {type: "Device", id: arg.fguid}
      ],
      query: ({fguid}) => `device/${fguid}`,
      transformResponse(resp:DeviceType) {
        if(resp.target_fguid) {
          devicesAdapter.updateOne(
            devicesInitialState,
            {
              id: resp.target_fguid,
              changes: { aliased_fguids: resp.aliased_fguids}
            }
          )
        };

        return devicesAdapter.setOne(
          devicesInitialState, 
          deviceResponseTransform(resp)
        )
      },
      async transformErrorResponse(resp: { status: number, data: Record<string, any> }, _, arg): Promise<ModelError> {
        return responseForAPIError(
          resp,
          {
            [DeviceAPIErrors.deviceDNEError]: await t(
              "device_mgmt.api_errors.device_dne",
              { fguid: arg.fguid }
            )
          }
        );
      }
    }),

    // Retrieve Devices
    fetchDevices: build.query<EntityState<IodyneDeviceDetail, string>, { org_id: string }>({
      //@ts-ignore
      providesTags: (result, _, arg) => [ ...result!.ids.map((fguid) => ({type: "Device", id:fguid}))],
      query: ({ org_id }) => ({
        url: 'devices',
        params: { org_id: org_id}
      }),
      transformResponse(resp: DeviceType[]) {
        //Parse and return device list
        var aliased_fguid_map:Record<string, string> = {};
        var parsed_list = resp.map((value: DeviceType, idx: number) => {
          if(value.target_fguid) {
            aliased_fguid_map[value.target_fguid] = value.fguid;
          }
          return deviceResponseTransform(value);
        });

        return devicesAdapter.setAll(devicesInitialState, parsed_list);
      },
      async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
        return responseForAPIError(
          resp,
          { [DeviceAPIErrors.accountDNEError]: await t("org_mgmt.api_errors.org_dne") }
        );
      }
    }),

    // Retrieve Device Secret
    fetchDeviceSecret: build.query<Uint8Array, {org_id: string, device_id:string, secret_key:string}>({
      providesTags: (result, _, {device_id, secret_key}) => [ 
        {type: "DeviceSecret", id:`${device_id}:${secret_key}`},
        {type: "Device", id:device_id}
      ],
      query: ({ org_id, device_id, secret_key }) => ({
        url: `device/${device_id}/secrets/${secret_key}`,
        params: { org_id: org_id}
      }),
      transformResponse(resp:IodyneDeviceSecret) {
        return Uint8Array.from(atob(resp.secret_bytes), c => c.charCodeAt(0));
      },
      async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
        return responseForAPIError(
          resp,
          { 
            [DeviceAPIErrors.accountDNEError]: await t("org_mgmt.api_errors.org_dne"),
            [DeviceAPIErrors.deviceSecretDNEError]: await t("device_mgmt.api_errors.secret_dne") 
          }
        );
      }
    }),

    // Modify Device
    modifyDevice: build.mutation<EntityState<IodyneDeviceDetail, string>, { org_id: string, fguid: string, device_name?: string, tags?:Array<string> }>({
      invalidatesTags: (_result, _error, { fguid }) => [
        { type: "Device", fguid: fguid },
        { type: "DeviceTags", fguid: fguid }
      ],
      query: ({ fguid, device_name, tags }) => ({
        url: `device/${fguid}`,
        method: "PUT",
        body: {
          ...(device_name && { device_name: device_name }),
          ...(tags && { tags: tags })
        }
      }),
      transformResponse(resp:DeviceType) {
        if(resp.target_fguid) {
          devicesAdapter.updateOne(
            devicesInitialState,
            {
              id: resp.target_fguid,
              changes: { aliased_fguids: resp.aliased_fguids}
            }
          )
        };

        return devicesAdapter.setOne(
          devicesInitialState, 
          deviceResponseTransform(resp)
        )
      },
      async transformErrorResponse(
        resp: { status: number, data: Record<string, any> },
        _meta,
        { org_id, fguid }
      ): Promise<ModelError> {
        return responseForAPIError(
          resp,
          {
            [DeviceAPIErrors.accountDNEError]: await t(
              "device_mgmt.api_errors.modify_device_failed_org_dne",
              { org_id: org_id }
            ),
            [DeviceAPIErrors.customerDeviceDNEError]: await t(
              "device_mgmt.api_errors.modify_device_failed_device_dne",
              { device_id: fguid }
            )
          }
        )
      }
    }),

    // Unregister Device from Account
    unregisterDevice: build.mutation<EntityState<IodyneDeviceDetail, string>, { org_id: string, fguid: string }>({
      invalidatesTags: (result, error, { org_id, fguid }) => [
        { type: "Device", fguid: fguid },
        { type: "DeviceTags", fguid: fguid }
      ],
      query: ({ org_id, fguid }) => ({
        url: `device/${fguid}`,
        method: "DELETE",
      }),
      transformResponse(resp: IodyneDeviceDetail, _meta, { org_id, fguid }) {
        resp.fguid = fguid;
        return devicesAdapter.removeOne(devicesInitialState, fguid);
      },
      async transformErrorResponse(
        resp: { status: number, data: Record<string, any> },
        _meta,
        { org_id, fguid }
      ): Promise<ModelError> {
        return responseForAPIError(
          resp,
          {
            [DeviceAPIErrors.accountDNEError]: await t(
              "device_mgmt.api_errors.unregister_device_failed_org_dne",
              { org_id: org_id }
            ),
            [DeviceAPIErrors.customerDeviceDNEError]: await t(
              "device_mgmt.api_errors.unregister_device_failed_device_dne",
              { device_id: fguid }
            )
          }
        )
      }
    }),

    //List All Device Tags
    listAllDeviceTags: build.query<Array<string>, { org_id: string }>({
      providesTags: (result, _, arg) => [{ type: "DeviceTags", id: arg.org_id }],
      query: ({ org_id }) => ({
        url: "tags",
        method: "GET",
        params: { "org_id" : org_id }
      })
    }),

    //List Device Containers
    listDeviceContainers: build.query<Array<ContainerInfo>, { org_id: string, fguid: string, include_hidden?:boolean }>({
      //@ts-ignore
      providesTags: (result, _, arg) => [{ type: "DeviceContainerList", id: arg.fguid }],
      query: ({ org_id, fguid }) => ({
         url: `device/${fguid}/containers`,
        params: { org_id: org_id}
      }),
      transformResponse(resp:ContainerInfo[], meta, {include_hidden}) {
        return resp.filter((container) => include_hidden || container.hidden == false)
      },
      async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
        return responseForAPIError(
          resp,
          { [DeviceAPIErrors.accountDNEError]: await t("org_mgmt.api_errors.org_dne") }
        );
      }
    })
  })
});

export const {
  useFetchDeviceDetailQuery,
  useFetchDevicesQuery,
  useUnregisterDeviceMutation,
  useModifyDeviceMutation,
  useListAllDeviceTagsQuery,
  useListDeviceContainersQuery,
  useFetchDeviceSecretQuery
} = devicesSlice;

export const devicesSelectors = devicesAdapter.getSelectors();
